/**
 * FieldIntelligencePage — /app/campo
 *
 * Mapa interativo de Field Intelligence:
 *  - Mapa MapLibre com clusters de contatos
 *  - Filtros: categoria e agrupamento (bairro | cidade)
 *  - Painel lateral: ranking de bairros/cidades por volume
 *  - Clique no bairro → foca o mapa e filtra pontos
 *  - Clique no pin → painel de detalhes do contato
 *  - Exportação CSV da área/categoria selecionada
 *  - Header com KPIs gerais
 */

import { useState } from 'react';
import {
  MapPin, Users, Star, Map as MapIcon,
  BarChart2, Loader2, ChevronRight, Building2, Download, Filter, Mic,
} from 'lucide-react';
import { ContactMap } from './ContactMap';
import { useVoiceNotesGeoPins } from '@/features/crm/hooks/useVoiceNotes';
import { ContactPinPanel } from './ContactPinPanel';
import { useGeoSummary, useContactMapPoints, useGeoStats, exportAreaContacts } from '../hooks/useGeoData';
import { useContactCategories } from '@/features/crm/hooks/useContacts';
import { useTenant } from '@/app/providers/TenantProvider';
import { useDefaultMapCenter } from '../hooks/useDefaultMapCenter';

// ── Componente ─────────────────────────────────────────────────────────────────

export function FieldIntelligencePage() {
  const { branding } = useTenant();
  const { center: defaultCenter, zoom: defaultZoom } = useDefaultMapCenter(branding.uf, branding.municipio);

  const [groupBy,           setGroupBy]           = useState<'neighborhood' | 'city'>('neighborhood');
  const [selectedArea,      setSelectedArea]       = useState<string | null>(null);
  const [focusNeighborhood, setFocusNeighborhood]  = useState<string | null>(null);
  const [categoryFilter,    setCategoryFilter]      = useState<string>('');
  const [selectedContactId, setSelectedContactId]  = useState<string | null>(null);
  const [exporting,         setExporting]          = useState(false);
  const [showVoicePins,     setShowVoicePins]      = useState(false);

  const { data: voicePins } = useVoiceNotesGeoPins(90);

  const { data: stats,      isLoading: statsLoading   } = useGeoStats();
  const { data: summary,    isLoading: summaryLoading  } = useGeoSummary(groupBy);
  const { data: categories = [] }                        = useContactCategories();
  const { data: points,     isLoading: pointsLoading   } = useContactMapPoints(
    selectedArea || categoryFilter
      ? {
          ...(selectedArea && groupBy === 'neighborhood' ? { neighborhood: selectedArea } : {}),
          ...(selectedArea && groupBy === 'city'         ? { city:         selectedArea } : {}),
          ...(categoryFilter ? { category: categoryFilter } : {}),
        }
      : undefined
  );

  const handleAreaClick = (label: string) => {
    if (selectedArea === label) {
      setSelectedArea(null);
      setFocusNeighborhood(null);
    } else {
      setSelectedArea(label);
      if (groupBy === 'neighborhood') setFocusNeighborhood(label);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      await exportAreaContacts({
        neighborhood: selectedArea && groupBy === 'neighborhood' ? selectedArea : undefined,
        city:         selectedArea && groupBy === 'city'         ? selectedArea : undefined,
        category:     categoryFilter || undefined,
        areaLabel:    selectedArea ?? categoryFilter ?? 'todos',
      });
    } finally {
      setExporting(false);
    }
  };

  const totalPoints = points?.length ?? 0;
  const hasCoords   = (stats?.withCoords ?? 0) > 0;
  const hasActiveFilter = !!selectedArea || !!categoryFilter;

  return (
    <div className="flex flex-col gap-6 px-4 sm:px-6 py-6 max-w-screen-xl mx-auto w-full">

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-bold text-ink flex items-center gap-2">
            <MapPin className="h-6 w-6 text-accent" />
            Field Intelligence
          </h1>
          <p className="text-sm text-ink-muted mt-1">
            Distribuição geográfica da sua base de contatos.
          </p>
        </div>

        {/* Controles */}
        <div className="flex flex-wrap items-center gap-2">

          {/* Filtro de categoria */}
          <div className="relative flex items-center">
            <Filter className="absolute left-2.5 h-3.5 w-3.5 text-ink-muted pointer-events-none" />
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="pl-8 pr-3 py-2 rounded-xl glass border border-hairline text-sm text-ink bg-transparent focus:outline-none focus:border-accent/50 transition-all cursor-pointer appearance-none"
            >
              <option value="">Todas categorias</option>
              {categories.map((c) => (
                <option key={c.slug} value={c.slug}>{c.label}</option>
              ))}
            </select>
          </div>

          {/* Toggle agrupamento */}
          <div className="flex gap-1 p-1 rounded-xl glass border border-hairline">
            {(['neighborhood', 'city'] as const).map((opt) => (
              <button
                key={opt}
                onClick={() => { setGroupBy(opt); setSelectedArea(null); setFocusNeighborhood(null); }}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold transition-all ${
                  groupBy === opt
                    ? 'bg-accent/15 text-accent shadow-glow-xs'
                    : 'text-ink-muted hover:text-ink hover:bg-white/5'
                }`}
              >
                {opt === 'neighborhood'
                  ? <><MapPin className="h-3.5 w-3.5" />Bairros</>
                  : <><Building2 className="h-3.5 w-3.5" />Cidades</>}
              </button>
            ))}
          </div>

          {/* Exportar */}
          <button
            onClick={handleExport}
            disabled={exporting}
            title={hasActiveFilter ? `Exportar contatos de "${selectedArea ?? categoryFilter}"` : 'Exportar todos os contatos visíveis'}
            className="flex items-center gap-1.5 px-3 py-2 rounded-xl glass border border-hairline text-sm font-semibold text-ink-muted hover:text-ink hover:border-accent/30 transition-all disabled:opacity-50 cursor-pointer"
          >
            {exporting
              ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
              : <Download className="h-3.5 w-3.5" />
            }
            <span className="hidden sm:inline">Exportar CSV</span>
          </button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {statsLoading ? (
          Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="h-20 rounded-2xl glass border border-hairline animate-pulse" />
          ))
        ) : (
          <>
            <KpiCard icon={Users}     label="Contatos ativos"   value={stats?.total               ?? 0} />
            <KpiCard icon={Star}      label="Lideranças"        value={stats?.leaders             ?? 0} color="text-accent" />
            <KpiCard icon={MapPin}    label="Bairros mapeados"  value={stats?.uniqueNeighborhoods ?? 0} />
            <KpiCard icon={Building2} label="Cidades"           value={stats?.uniqueCities        ?? 0} />
          </>
        )}
      </div>

      {/* Aviso sem coordenadas */}
      {!statsLoading && !hasCoords && (
        <div className="flex items-start gap-3 px-4 py-3 rounded-xl bg-amber-400/5 border border-amber-400/20">
          <MapIcon className="h-4 w-4 text-amber-400 shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-ink">Nenhum contato com coordenadas</p>
            <p className="text-xs text-ink-muted mt-0.5">
              Importe contatos com colunas <code className="text-accent font-mono text-[10px]">latitude</code> e{' '}
              <code className="text-accent font-mono text-[10px]">longitude</code> para ver marcadores no mapa.
              O painel lateral funciona mesmo sem coordenadas.
            </p>
          </div>
        </div>
      )}

      {/* Filtro ativo — pill informativo */}
      {hasActiveFilter && (
        <div className="flex flex-wrap items-center gap-2 -mt-2">
          <span className="text-xs text-ink-subtle">Filtros ativos:</span>
          {categoryFilter && (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-accent/10 border border-accent/25 px-2.5 py-0.5 text-xs text-accent font-semibold">
              {categories.find((c) => c.slug === categoryFilter)?.label ?? categoryFilter}
              <button onClick={() => setCategoryFilter('')} className="hover:text-white ml-0.5">×</button>
            </span>
          )}
          {selectedArea && (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-accent/10 border border-accent/25 px-2.5 py-0.5 text-xs text-accent font-semibold">
              {groupBy === 'neighborhood' ? 'Bairro:' : 'Cidade:'} {selectedArea}
              <button onClick={() => { setSelectedArea(null); setFocusNeighborhood(null); }} className="hover:text-white ml-0.5">×</button>
            </span>
          )}
        </div>
      )}

      {/* Mapa + Painel lateral */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" style={{ minHeight: '520px' }}>

        {/* Mapa */}
        <div className="lg:col-span-2 glass border border-hairline rounded-2xl overflow-hidden relative" style={{ minHeight: '480px' }}>
          {pointsLoading && (
            <div className="absolute inset-0 flex items-center justify-center z-10 bg-midnight/50 rounded-2xl">
              <Loader2 className="h-6 w-6 animate-spin text-accent" />
            </div>
          )}
          <ContactMap
            points={points ?? []}
            voicePins={voicePins ?? []}
            showVoicePins={showVoicePins}
            focusNeighborhood={focusNeighborhood}
            onPointClick={(pt) => setSelectedContactId(pt.id)}
            defaultCenter={defaultCenter}
            defaultZoom={defaultZoom}
            className="h-full"
          />

          {/* Toggle de notas de voz */}
          <button
            type="button"
            onClick={() => setShowVoicePins((v) => !v)}
            className={`absolute top-3 left-3 z-10 flex items-center gap-2 rounded-xl px-3 py-2 text-xs font-semibold border transition-all cursor-pointer ${
              showVoicePins
                ? 'bg-accent/15 border-accent/30 text-accent shadow-glow-xs'
                : 'glass border-hairline text-ink-muted hover:text-ink hover:border-accent/20'
            }`}
            title={`${showVoicePins ? 'Ocultar' : 'Mostrar'} notas de voz no mapa (${voicePins?.length ?? 0} pins)`}
          >
            <Mic className="h-3.5 w-3.5" />
            <span>Notas de voz</span>
            {(voicePins?.length ?? 0) > 0 && (
              <span className="font-mono text-[10px] px-1.5 py-0.5 rounded-full bg-white/8">
                {voicePins!.length}
              </span>
            )}
          </button>

          {/* Legenda de sentimento — só aparece quando layer está ativa */}
          {showVoicePins && (voicePins?.length ?? 0) > 0 && (
            <div className="absolute top-3 right-14 glass border border-hairline rounded-xl px-3 py-2 flex flex-wrap gap-x-3 gap-y-1 text-[10px] z-10">
              <span className="text-ink-subtle font-mono uppercase tracking-wider">Sentimento</span>
              {[
                { color: '#10B981', label: 'Positivo' },
                { color: '#94A3B8', label: 'Neutro'   },
                { color: '#EF4444', label: 'Negativo' },
                { color: '#FBBF24', label: 'Misto'    },
              ].map((s) => (
                <span key={s.label} className="flex items-center gap-1 text-ink-muted">
                  <span className="h-2 w-2 rounded-full" style={{ backgroundColor: s.color }} />
                  {s.label}
                </span>
              ))}
            </div>
          )}

          {/* Legenda de categorias */}
          {categories.length > 0 && (
            <div className="absolute bottom-3 left-3 glass border border-hairline rounded-xl px-3 py-2 flex flex-wrap gap-x-4 gap-y-1 max-w-[280px]">
              {categories.slice(0, 5).map((cat) => {
                const colorMatch = cat.color_class.match(/text-(\w+-\d+)/);
                const colorKey   = colorMatch?.[1];
                return (
                  <button
                    key={cat.slug}
                    onClick={() => setCategoryFilter(categoryFilter === cat.slug ? '' : cat.slug)}
                    className={`flex items-center gap-1.5 transition-opacity ${categoryFilter && categoryFilter !== cat.slug ? 'opacity-30' : 'opacity-100'}`}
                    title={`Filtrar por ${cat.label}`}
                  >
                    <div
                      className={`h-2 w-2 rounded-full ${cat.color_class.replace(/border-\S+/g, '').replace(/text-\S+/g, '').replace(/bg-(\S+)\/15/g, 'bg-$1')}`}
                    />
                    <span className="text-[10px] text-ink-muted">{cat.label}</span>
                  </button>
                );
                void colorKey; // evita lint unused
              })}
            </div>
          )}
        </div>

        {/* Painel lateral: ranking */}
        <div className="glass border border-hairline rounded-2xl overflow-hidden flex flex-col">
          <div className="px-4 py-3 border-b border-hairline flex items-center justify-between shrink-0">
            <div className="flex items-center gap-2">
              <BarChart2 className="h-4 w-4 text-accent" />
              <span className="text-sm font-semibold text-ink">
                {groupBy === 'neighborhood' ? 'Bairros' : 'Cidades'}
              </span>
            </div>
            {selectedArea && (
              <button
                onClick={() => { setSelectedArea(null); setFocusNeighborhood(null); }}
                className="text-xs text-accent hover:underline cursor-pointer"
              >
                Limpar filtro
              </button>
            )}
          </div>

          <div className="flex-1 overflow-y-auto divide-y divide-hairline">
            {summaryLoading ? (
              <div className="p-4 space-y-2">
                {Array.from({ length: 8 }).map((_, i) => (
                  <div key={i} className="h-10 rounded-lg bg-white/5 animate-pulse" style={{ opacity: 1 - i * 0.1 }} />
                ))}
              </div>
            ) : !summary?.length ? (
              <div className="flex flex-col items-center justify-center h-40 gap-2 text-ink-muted">
                <MapPin className="h-8 w-8 opacity-30" />
                <p className="text-sm">Nenhum dado disponível</p>
              </div>
            ) : (
              summary.map((row) => {
                const isSelected = selectedArea === row.label;
                const maxCount   = summary[0].contact_count;
                const pct        = maxCount > 0 ? (row.contact_count / maxCount) * 100 : 0;

                return (
                  <button
                    key={row.label}
                    onClick={() => handleAreaClick(row.label)}
                    className={`w-full px-4 py-3 text-left hover:bg-white/5 transition-colors group cursor-pointer ${
                      isSelected ? 'bg-accent/8 border-l-2 border-accent' : ''
                    }`}
                  >
                    <div className="flex items-center justify-between mb-1.5">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className={`text-xs font-semibold truncate ${isSelected ? 'text-accent' : 'text-ink'}`}>
                          {row.label}
                        </span>
                        {row.city && groupBy === 'neighborhood' && (
                          <span className="text-[10px] text-ink-subtle truncate hidden sm:block">{row.city}</span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <span className="text-xs font-bold text-ink">{row.contact_count}</span>
                        {row.leader_count > 0 && (
                          <span className="text-[10px] text-accent font-semibold">{row.leader_count} lid.</span>
                        )}
                        <ChevronRight className={`h-3 w-3 transition-colors ${isSelected ? 'text-accent' : 'text-ink-subtle group-hover:text-ink'}`} />
                      </div>
                    </div>

                    {/* Barra de progresso relativa */}
                    <div className="h-1 w-full rounded-full bg-white/5 overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all ${isSelected ? 'bg-accent' : 'bg-accent/40'}`}
                        style={{ width: `${pct}%` }}
                      />
                    </div>

                    {row.avg_engagement > 0 && (
                      <p className="text-[10px] text-ink-subtle mt-1">
                        Engajamento médio: {row.avg_engagement}
                      </p>
                    )}
                  </button>
                );
              })
            )}
          </div>

          {/* Footer: total de pontos visíveis */}
          {totalPoints > 0 && (
            <div className="px-4 py-2.5 border-t border-hairline shrink-0">
              <p className="text-[11px] text-ink-muted">
                {totalPoints} contato{totalPoints !== 1 ? 's' : ''} com coordenadas
                {selectedArea ? ` em "${selectedArea}"` : ' visíveis no mapa'}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Painel de detalhe do contato (abre quando clica num pin) */}
      <ContactPinPanel
        contactId={selectedContactId}
        onClose={() => setSelectedContactId(null)}
      />
    </div>
  );
}

// ── KPI card ───────────────────────────────────────────────────────────────────

function KpiCard({ icon: Icon, label, value, color = 'text-ink' }: {
  icon: React.ElementType; label: string; value: number; color?: string;
}) {
  return (
    <div className="glass border border-hairline rounded-2xl px-5 py-4 flex items-center gap-4">
      <div className="h-10 w-10 rounded-xl bg-accent/10 flex items-center justify-center shrink-0">
        <Icon className="h-5 w-5 text-accent" />
      </div>
      <div>
        <p className={`text-2xl font-bold font-display ${color}`}>
          {value.toLocaleString('pt-BR')}
        </p>
        <p className="text-xs text-ink-muted">{label}</p>
      </div>
    </div>
  );
}
